import { ensureRobotoFont } from "@/modules/canvas/pdfFonts";
import { Context as SvgContext } from "svgcanvas";

export class CanvasDownloader {
    tab
    maxWidth
    maxHeight

    constructor(tab) {
        this.tab = tab
    }

    /**
     * Compute the schema bounding box from the tab data so we can size an
     * export surface before rendering. Mirrors the logic in downloadPdf.
     */
    _computeSchemaBounds() {
        const canvas = this.tab.canvas
        const items = this.tab.schema.schema_data.tables.concat(this.tab.schema.schema_data.notes)
        let minX = 0, minY = 0, maxX = 1000, maxY = 800
        let first = true
        for (const data of items) {
            if (data.checked === false) continue
            if (data.resource === 'note' && !data.content) continue
            const left = data.left || 0
            const top = data.top || 0
            const right = left + 200
            const bottom = top + 100
            if (first) { minX = left; minY = top; maxX = right; maxY = bottom; first = false }
            if (left < minX) minX = left
            if (top < minY) minY = top
            if (right > maxX) maxX = right
            if (bottom > maxY) maxY = bottom
        }
        // Blend with live canvas bounds (post-layout) for accuracy.
        minX = Math.min(minX, canvas.minX)
        minY = Math.min(minY, canvas.minY)
        maxX = Math.max(maxX, canvas.maxX)
        maxY = Math.max(maxY, canvas.maxY)
        return { minX, minY, maxX, maxY }
    }

    /**
     * Single high-DPI PNG export. Uses the same vector renderer as PDF export
     * (ApmCanvas.drawForExport) into an offscreen canvas, then encodes once to
     * PNG via toBlob. No tiling, no ZIP, no mutation of the live canvas.
     *
     * @param {object} opts
     * @param {string} [opts.name]
     * @param {string} [opts.bg]     background color (CSS color string)
     * @param {number} [opts.scale]  DPI multiplier (default 2 for retina-crisp)
     */
    async downloadImage({ name = 'generated', bg = '#fff', scale = 2 } = {}) {
        const canvas = this.tab.canvas
        const padding = 60

        const { minX, minY, maxX, maxY } = this._computeSchemaBounds()
        const baseWidth = (maxX - minX) + padding * 2
        const baseHeight = (maxY - minY) + padding * 2

        // Cap output at 16000px longest edge to stay under browser canvas limits.
        const maxDim = 16000
        const scaleCap = Math.min(scale, maxDim / Math.max(baseWidth, baseHeight))
        const dpr = Math.max(1, scaleCap)

        const offscreen = document.createElement('canvas')
        offscreen.width = Math.round(baseWidth * dpr)
        offscreen.height = Math.round(baseHeight * dpr)
        const ctx = offscreen.getContext('2d')

        // Background
        ctx.save()
        ctx.fillStyle = bg
        ctx.fillRect(0, 0, offscreen.width, offscreen.height)
        ctx.restore()

        // World -> image transform: scale for DPI, then translate so (minX,minY)
        // lands at (padding,padding).
        ctx.save()
        ctx.scale(dpr, dpr)
        ctx.translate(-minX + padding, -minY + padding)

        canvas.drawForExport(ctx, this.tab)

        ctx.restore()

        // Watermark (rendered in image-space, not affected by dpr scale)
        ctx.save()
        ctx.font = `italic ${12 * dpr}px Roboto, "Helvetica Neue", Arial, sans-serif`
        ctx.fillStyle = 'rgba(0, 0, 0, 0.35)'
        ctx.textBaseline = 'bottom'
        ctx.fillText(
            `Generated with ${window.location.hostname}`,
            10 * dpr,
            offscreen.height - 10 * dpr
        )
        ctx.restore()

        const blob = await new Promise((resolve, reject) =>
            offscreen.toBlob(b => b ? resolve(b) : reject(new Error('toBlob failed')), 'image/png')
        )

        this._triggerDownload(blob, `${name}.png`)
    }

    /**
     * Vector SVG export. Renders the schema through svgcanvas (Canvas 2D ->
     * SVG) into a single self-contained .svg file. Output is fully vector,
     * editable in Illustrator/Inkscape/Figma, tiny compared to PNG, and
     * infinitely scalable.
     *
     * @param {object} opts
     * @param {string} [opts.name]
     * @param {string} [opts.bg]
     */
    async downloadSvg({ name = 'generated', bg = '#fff' } = {}) {
        const canvas = this.tab.canvas
        const padding = 60

        const { minX, minY, maxX, maxY } = this._computeSchemaBounds()
        const width = (maxX - minX) + padding * 2
        const height = (maxY - minY) + padding * 2

        const ctx = new SvgContext({ width, height })

        if (bg) {
            ctx.save()
            ctx.fillStyle = bg
            ctx.fillRect(0, 0, width, height)
            ctx.restore()
        }

        ctx.save()
        ctx.translate(-minX + padding, -minY + padding)
        canvas.drawForExport(ctx, this.tab)
        ctx.restore()

        // Watermark
        ctx.save()
        ctx.font = `italic 12px Roboto, "Helvetica Neue", Arial, sans-serif`
        ctx.fillStyle = 'rgba(0, 0, 0, 0.35)'
        ctx.textBaseline = 'bottom'
        ctx.fillText(`Generated with ${window.location.hostname}`, 10, height - 10)
        ctx.restore()

        const svg = ctx.getSerializedSvg(true)
        const blob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' })
        this._triggerDownload(blob, `${name}.svg`)
    }

    _triggerDownload(blob, filename) {
        const link = document.createElement('a')
        const url = window.URL.createObjectURL(blob)
        link.href = url
        link.download = filename
        link.click()
        setTimeout(() => window.URL.revokeObjectURL(url), 1000)
    }

    /**
     * Vector PDF export. Renders the schema directly to jsPDF via its
     * Canvas 2D shim (doc.context2d) instead of rasterizing the canvas.
     * Output is a true vector PDF with selectable text, crisp at any zoom,
     * and typically an order of magnitude smaller than the image-based version.
     */
    async downloadPdf({ name = 'generated', bg = '#fff' }) {
        const canvas = this.tab.canvas
        const padding = 50

        const { minX, minY, maxX, maxY } = this._computeSchemaBounds()
        let width = (maxX - minX) + padding * 2
        let height = (maxY - minY) + padding * 2

        // jsPDF max page size is ~14400pt. With unit:'px' and px_scaling,
        // 1px ≈ 1pt, so clamp at 14000 with a scale factor to keep fidelity.
        const maxDim = 14000
        const scale = Math.min(1, maxDim / Math.max(width, height))
        if (scale < 1) {
            width = Math.round(width * scale)
            height = Math.round(height * scale)
        }

        const { jsPDF } = await import('jspdf')

        const doc = new jsPDF({
            orientation: width > height ? 'landscape' : 'portrait',
            unit: 'px',
            hotfixes: ['px_scaling'],
            format: [width, height]
        })

        await ensureRobotoFont(doc)

        const ctx = doc.context2d
        ctx.autoPaging = false
        ctx.pageWrapXEnabled = false
        ctx.pageWrapYEnabled = false

        if (bg) {
            ctx.save()
            ctx.fillStyle = bg
            ctx.fillRect(0, 0, width, height)
            ctx.restore()
        }

        ctx.save()
        if (scale < 1) ctx.scale(scale, scale)
        // Offset so (minX, minY) maps to (padding, padding) in page space
        ctx.translate(-minX + padding, -minY + padding)

        canvas.drawForExport(ctx, this.tab)

        ctx.restore()

        // Watermark (vector text, once, bottom-left of the page)
        try { doc.setFont('Roboto', 'italic', 'normal') } catch { doc.setFont('helvetica', 'italic') }
        doc.setFontSize(10)
        doc.setTextColor(150)
        doc.text(`Generated with ${window.location.hostname}`, 10, height - 8)

        // jsPDF's context2d can leave a blank first page when it advances
        // internally on first write. Collapse to the page(s) that actually
        // contain content.
        while (doc.getNumberOfPages() > 1) {
            doc.deletePage(1)
        }

        doc.save(`${name}.pdf`)
    }
}
